import Cookies from 'js-cookie';
import extractQuoteCodeFromReference from '../utils/extractQuoteCodeFromReference/extractQuoteCodeFromReference';
import {
  GetCostBreakdownResponseModel,
  GetQuotationRequestResponseModel,
  QuoteAcceptanceRequestPayload,
  QuoteAcceptanceResponseModel,
  RequestQuotationPayload,
  RequestQuotationResponseModel,
} from './quote.interfaces';

const baseUrl = `${process.env.GATSBY_QUOTE_API_URL}/quotations`;
const headers = {
  'Content-Type': 'application/json',
};

export interface HttpResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export interface HttpError {
  message: string;
  status: number;
  statusText: string;
  data?: unknown;
}

const getErrorMessage = (error: HttpError): string => {
  if (typeof error.data === 'object' && error.data !== null && 'message' in error.data) {
    const message = (error.data as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }

  return error.statusText || error.message || 'Request failed';
};

const request = async <T>(
  url: string,
  init?: RequestInit
): Promise<HttpResponse<T>> => {
  const response = await fetch(url, init);
  const contentType = response.headers.get('content-type') || '';

  let data: unknown = null;

  if (contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = text || null;
  }

  if (!response.ok) {
    const error: HttpError = {
      message: 'Request failed',
      status: response.status,
      statusText: response.statusText,
      data,
    };

    error.message = getErrorMessage(error);
    throw error;
  }

  return {
    data: data as T,
    status: response.status,
    headers: response.headers,
  };
};

export async function requestQuotation(
  data: RequestQuotationPayload
): Promise<HttpResponse<RequestQuotationResponseModel>> {
  const url = `${baseUrl}/requests`;

  return request<RequestQuotationResponseModel>(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function getQuotation(
  quoteReference: string,
  trackingRef?: string,
  trackingReference002?: string
): Promise<HttpResponse<GetQuotationRequestResponseModel>> {
  const url = `${baseUrl}/${encodeURIComponent(
    encodeURIComponent(quoteReference)
  )}/request`;

  const params = new URLSearchParams();

  if (trackingRef) {
    params.append('trackingRef', trackingRef);
  }

  if (trackingReference002) {
    params.append('trackingReference002', trackingReference002);
  }

  const query = params.toString();
  const response = await request<GetQuotationRequestResponseModel>(
    query ? `${url}?${query}` : url,
    {
      method: 'GET',
      headers,
    }
  );

  const previousQuotationReference = extractQuoteCodeFromReference(
    response.data.quoteReference
  );

  if (previousQuotationReference) {
    Cookies.set('previousQuotationReference', previousQuotationReference, {
      expires: 7,
    });
  }

  return response;
}

export async function getCostBreakdown(
  quoteReference: string,
  trackingRef: string,
  trackingReference002?: string
): Promise<HttpResponse<GetCostBreakdownResponseModel>> {
  const url = `${baseUrl}/${encodeURIComponent(
    encodeURIComponent(quoteReference)
  )}/costBreakdown`;

  const params = new URLSearchParams();

  if (trackingRef) {
    params.append('trackingRef', trackingRef);
  }

  if (trackingReference002) {
    params.append('trackingReference002', trackingReference002);
  }

  const query = params.toString();

  return request<GetCostBreakdownResponseModel>(
    query ? `${url}?${query}` : url,
    {
      method: 'GET',
      headers,
    }
  );
}

export async function postQuoteAcceptance(
  quoteReference: string,
  quoteInformation: QuoteAcceptanceRequestPayload,
  trackingRef: string,
  trackingReference002?: string
): Promise<HttpResponse<QuoteAcceptanceResponseModel>> {
  const url = `${baseUrl}/${encodeURIComponent(
    encodeURIComponent(quoteReference)
  )}/acceptance/`;

  const params = new URLSearchParams();

  if (trackingRef) {
    params.append('trackingRef', trackingRef);
  }

  if (trackingReference002) {
    params.append('trackingReference002', trackingReference002);
  }

  const query = params.toString();

  return request<QuoteAcceptanceResponseModel>(
    query ? `${url}?${query}` : url,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(quoteInformation),
    }
  );
}
